import { useMemo } from "react";
import { useLocation, useParams } from "wouter";
import { cmdkRegistry, type CmdkGroup } from "../cmdk_registry.js";
import { createCmdkItem } from "../cmdk_item.js";
import { getRoutes } from "../route_registry.js";
import { getPhases } from "../registry.js";

const EXCLUDED_ROUTES = new Set(["login", "org-root"]);

function isNavigableRoute(path: string): boolean {
  // Exclude routes with multiple params (detail pages)
  const paramCount = (path.match(/:/g) || []).length;
  return paramCount <= 1;
}

function useNavigateCmdkItems(): CmdkGroup[] {
  const [, navigate] = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const routes = getRoutes();
  const phases = getPhases();

  return useMemo(() => {
    // Global routes (no :orgSlug)
    const globalItems = routes
      .filter(
        route =>
          !route.path.includes(":orgSlug") &&
          !EXCLUDED_ROUTES.has(route.type) &&
          isNavigableRoute(route.path),
      )
      .map(route =>
        createCmdkItem({
          value: route.type,
          label: route.label,
          onSelect: () => navigate(route.path),
        }),
      );

    // Org routes (with :orgSlug only)
    const orgItems = orgSlug
      ? routes
          .filter(
            route =>
              route.path.includes(":orgSlug") &&
              !EXCLUDED_ROUTES.has(route.type) &&
              isNavigableRoute(route.path),
          )
          .map(route =>
            createCmdkItem({
              value: route.type,
              label: route.label,
              onSelect: () => navigate(route.path.replace(":orgSlug", orgSlug)),
            }),
          )
      : [];

    // Phase routes
    const phaseItems = orgSlug
      ? phases.map(phase =>
          createCmdkItem({
            value: phase.type,
            label: phase.type.charAt(0).toUpperCase() + phase.type.slice(1),
            onSelect: () => navigate(`/o/${orgSlug}/${phase.type}`),
          }),
        )
      : [];

    return [
      {
        heading: "Navigate",
        items: [...globalItems, ...orgItems, ...phaseItems],
      },
    ];
  }, [orgSlug, routes, phases, navigate]);
}

cmdkRegistry.register({
  type: "navigate",
  useItems: useNavigateCmdkItems,
});
